import type { ResourceKind } from "@cc/protocol";
import type { Dict } from "./i18n";

/**
 * Presentation metadata for the two arena slots.
 *
 * The visual language is deliberately simple: slot 0 is red, slot 1 is purple,
 * and nothing else on screen uses those hues. In a fast-moving arena, colour is
 * the fastest way for a viewer to answer "which one is winning", so it must
 * never be ambiguous.
 */
export interface SlotTheme {
  readonly key: "red" | "purple";
  readonly color: string;
  readonly dim: string;
  readonly glow: string;
  readonly label: string;
}

export const SLOT_THEMES: readonly [SlotTheme, SlotTheme] = [
  {
    key: "red",
    color: "#ff2d55",
    dim: "#7a1528",
    glow: "rgba(255,45,85,0.55)",
    label: "A",
  },
  {
    key: "purple",
    color: "#8b5cf6",
    dim: "#3d2a6b",
    glow: "rgba(139,92,246,0.55)",
    label: "B",
  },
];

export function slotTheme(slot: number): SlotTheme {
  return SLOT_THEMES[slot === 1 ? 1 : 0];
}

export interface ResourceStyle {
  readonly glyph: string;
  readonly color: string;
}

export const RESOURCE_STYLES: Record<ResourceKind, ResourceStyle> = {
  crystal: { glyph: "◆", color: "#38bdf8" },
  energy: { glyph: "✦", color: "#fbbf24" },
  shield: { glyph: "⬢", color: "#34d399" },
  bomb: { glyph: "⬤", color: "#f97316" },
  blade: { glyph: "✸", color: "#e879f9" },
  trap: { glyph: "☠", color: "#ef4444" },
};

/**
 * Icons for the decision timeline.
 *
 * These are *structured* summaries, never the model's private chain of thought.
 * docs/001.md is explicit about that: the interesting artefact is what a cricket
 * decided and the tactical note it produced, not its reasoning tokens.
 *
 * Icons are language-independent, so they live outside the dictionary.
 */
export function eventIcon(type: string): string {
  switch (type) {
    case "decision":
      return "🧠";
    case "collect":
      return "💎";
    case "attack":
      return "⚔️";
    case "miss":
      return "❌";
    case "trap":
      return "💀";
    case "detect":
      return "🔍";
    case "scout":
      return "👁";
    case "interrupt":
      return "✂️";
    case "collectMiss":
      return "❌";
    case "kill":
      return "☠️";
    case "spawn":
      return "✨";
    case "flee":
      return "💨";
    case "exchange":
      return "💱";
    case "use":
      return "💣";
    case "bomb":
      return "💥";
    case "blade":
      return "🌀";
    default:
      return "•";
  }
}

export function phaseFor(
  timeRemainingTicks: number,
  d: Dict,
): { label: string; final: boolean } {
  const s = timeRemainingTicks / 10;
  // Tuned for the default 90-second match.
  if (s > 60) return { label: d.phaseEarly, final: false };
  if (s > 30) return { label: d.phaseMid, final: false };
  if (s > 12) return { label: d.phaseLate, final: false };
  return { label: d.phaseFinal, final: true };
}

/** Remaining abstract seconds (= decision steps left when interval is 1s). */
export function formatClock(ticks: number): string {
  const total = Math.floor(Math.max(0, ticks) / 10);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
